import React, { useLayoutEffect, useRef } from 'react';
import type { SmartScheduleEvent } from '@/features/smartSchedule/model';
import { formatQuickMoveClock, layoutQuickMovePreview, QuickMoveDraft, QuickMoveSource } from '@/features/scheduleQuickMove';

export const QuickMovePreview = ({ source, draft, events, overlapIds }: {
    source: QuickMoveSource;
    draft: QuickMoveDraft;
    events: SmartScheduleEvent[];
    overlapIds: Set<string>;
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const layout = layoutQuickMovePreview(source, draft, events);
    const duration = layout.end - layout.start;
    const position = (start: number, end: number) => ({
        left: `${(start - layout.start) / duration * 100}%`,
        width: `${Math.max(0, end - start) / duration * 100}%`,
    });
    const clockRange = (start: number, end: number) => `${formatQuickMoveClock(start)}–${formatQuickMoveClock(end)}`;
    const originalLane = layout.laneCount;
    const targetLane = originalLane + (layout.original ? 1 : 0);

    useLayoutEffect(() => {
        const scroll = scrollRef.current;
        if (!scroll) return;
        const center = ((layout.target.start + layout.target.end) / 2 - layout.start) / duration * scroll.scrollWidth;
        scroll.scrollLeft = Math.max(0, center - scroll.clientWidth / 2);
    }, [duration, layout.start, layout.target.start, layout.target.end]);

    return <>
        <div className="quick-move-preview-scroll" ref={scrollRef} tabIndex={0} aria-label="강사 일정 시간축" aria-describedby="quick-move-preview-scroll-hint">
            <div className="quick-move-preview-timeline" style={{ minWidth: duration * 1.5 }}>
                <div className="quick-move-preview-hours">{layout.ticks.map((minute) => <span key={minute} style={{ left: `${(minute - layout.start) / duration * 100}%` }}>{formatQuickMoveClock(minute)}</span>)}</div>
                <div className="quick-move-preview-track" style={{ height: (targetLane + 1) * 28 + 6, backgroundSize: `${60 / duration * 100}% 100%` }}>
                    {layout.blocks.map(({ event, start, end, lane }) => <span key={event.id}
                        className={`quick-move-preview-event quick-move-preview-event--${event.kind}${overlapIds.has(event.id) ? ' is-overlapping' : ''}`}
                        style={{ ...position(start, end), top: lane * 28 + 3 }} title={`${event.title} ${clockRange(start, end)}`}>
                        {event.title}
                    </span>)}
                    {layout.original && <span className="quick-move-preview-original" style={{ ...position(layout.original.start, layout.original.end), top: originalLane * 28 + 3 }} title={`원래 위치 ${source.title} ${clockRange(layout.original.start, layout.original.end)}`}>{source.title}</span>}
                    {layout.target.end > layout.target.start && <span className="quick-move-preview-target" style={{ ...position(layout.target.start, layout.target.end), top: targetLane * 28 + 3 }} title={`새 위치 ${source.title} ${clockRange(layout.target.start, layout.target.end)}`}>{source.title}</span>}
                </div>
            </div>
        </div>
        <div className="quick-move-preview-legend"><span>기존 일정 {events.length}개{layout.original && ' · 회색 점선: 원래 위치'}</span><strong>새 위치 {clockRange(draft.startMinutes, draft.endMinutes)}</strong></div>
        <small id="quick-move-preview-scroll-hint" className="quick-move-preview-scroll-hint">좌우로 스크롤해 전체 일정을 볼 수 있어요.</small>
    </>;
};
